import React from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    ValidationResult,
    FormValidationSummary,
    FormTextField,
    FormNumberTextField
} from "sprint-common-web";

import {
    AudioFileRecordTechnical,
    ensureAudioFileRecordDetails
} from "../../../../../../../common/file-record-details/FileRecordDetails";
import { validateDuration } from "../../../../../../common/utils/validateDuration";
import { useFileRecordDetailsState } from "../../../../../hooks/useFileRecordDetailsState";

export interface AudioTechnicalFormProps {
    isLoading: boolean;
    onCancel?(): void;
    validationResult?: ValidationResult;
    onSubmit(technicalInfo: AudioFileRecordTechnical): void;
}

interface AudioTechnicalInfoFormInputs {
    audioFormat: string;
    resolution: string;
    duration: string;
    sampleRate: number;
    channelsCount: number;
    audioBitRate: number;
}

export const AudioTechnicalForm = (props: AudioTechnicalFormProps): JSX.Element => {
    const { fileRecordDetails } = useFileRecordDetailsState();

    ensureAudioFileRecordDetails(fileRecordDetails);

    const { handleSubmit, errors, control } = useForm<AudioTechnicalInfoFormInputs>({
        defaultValues: {
            audioFormat: fileRecordDetails.technical?.audioFormat,
            resolution: fileRecordDetails.technical?.resolution,
            duration: fileRecordDetails.technical?.duration,
            sampleRate: fileRecordDetails.technical?.sampleRate,
            audioBitRate: fileRecordDetails.technical?.audioBitRate,
            channelsCount: fileRecordDetails.technical?.channelsCount
        }
    });

    const closeClearedAndCallOnClose = () => {
        if (props.onCancel) {
            props.onCancel();
        }
    };

    const callOnSubmit = (formInputs: AudioTechnicalInfoFormInputs) => {
        props.onSubmit({
            audioFormat: formInputs.audioFormat,
            resolution: formInputs.resolution,
            duration: formInputs.duration,
            sampleRate: formInputs.sampleRate,
            audioBitRate: formInputs.audioBitRate,
            channelsCount: formInputs.channelsCount
        });
    };

    return (
        <Form onSubmit={handleSubmit(callOnSubmit)}>
            <FormFields>
                <FormTextField label="Audio format" name="audioFormat" control={control} errors={errors} />
                <FormTextField
                    rules={{ validate: validateDuration }}
                    label="Duration"
                    name="duration"
                    control={control}
                    errors={errors}
                    mask="99:99:99"
                />
                <FormNumberTextField label="Sample rate" name="sampleRate" control={control} errors={errors} />
                <FormNumberTextField label="Audio bitrate" name="audioBitRate" control={control} errors={errors} />
                <FormNumberTextField label="Channels count" name="channelsCount" control={control} errors={errors} />
                {props.validationResult && <FormValidationSummary validationResult={props.validationResult} />}
                <FormActions onCancel={closeClearedAndCallOnClose} isLoading={props.isLoading} />
            </FormFields>
        </Form>
    );
};
